import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, ArrowRight, MapPin, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SiteHeader } from '../components/home/SiteHeader';
import { SiteFooter } from '../components/home/SiteFooter';
import { Breadcrumb } from '../components/Breadcrumb';
import { EstimateCTA } from '../components/CrossSellCTA';
import { trackClick } from '../utils/analytics';
import { localPages } from '../data/localPagesData';

const LocalServicePage = () => {
  const { pageSlug } = useParams();
  const routerLocation = useLocation();
  const slug = pageSlug || routerLocation.pathname.replace(/^\//, '');
  const page = localPages[slug];

  useEffect(() => {
    if (page) document.title = page.metaTitle;
  }, [page]);

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-zinc-900 mb-4">Page Not Found</h1>
          <Link to="/" className="text-red-600 hover:text-red-700 font-semibold">Back to Home</Link>
        </div>
      </div>
    );
  }

  const faqSchema = page.faqs?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page.faqs.map(f => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "North Texas Welding and Fabrication, LLC",
    "telephone": "817-723-5006",
    "email": "Northtexasweldingandfab@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Springtown",
      "addressRegion": "TX",
      "postalCode": "76082"
    },
    "areaServed": { "@type": "City", "name": `${page.city}, TX` },
    "url": `https://ntxpipefence.com/${slug}`
  };

  const breadcrumbs = [
    ...(page.parentHub ? [{ label: page.parentHub.label, href: page.parentHub.href }] : []),
    { label: `${page.city}, TX` }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`local-${slug}`}>
      <Helmet>
        <meta name="description" content={page.metaDesc} />
        <link rel="canonical" href={`https://ntxpipefence.com/${slug}`} />
        <meta property="og:title" content={page.metaTitle} />
        <meta property="og:description" content={page.metaDesc} />
        <meta property="og:url" content={`https://ntxpipefence.com/${slug}`} />
      </Helmet>

      <SiteHeader mobileMenuOpen={false} setMobileMenuOpen={() => {}} />

      <div className="pt-24">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Hero */}
      <section className="bg-zinc-900 py-14 md:py-18 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzI2MjYyNiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={16} className="text-red-500" />
                <span className="text-red-500 text-sm font-semibold">{page.city}, {page.county}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{page.h1}</h1>
              <p className="text-zinc-300 leading-relaxed mb-8">{page.intro}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/instant-estimate"
                  onClick={() => trackClick('Instant Estimate - Local', `/${pageSlug}`)}
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors"
                >
                  Get an Instant Estimate <ArrowRight size={16} />
                </Link>
                <a href="tel:8177235006" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-black font-semibold px-7 py-3.5 rounded-lg transition-colors">
                  <Phone size={16} /> 817-723-5006
                </a>
              </div>
            </div>
            <div>
              <img src={page.heroImage} alt={page.h1} className="rounded-xl shadow-2xl w-full object-cover aspect-[4/3]" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      {/* Local Content */}
      {page.localContent && (
        <section className="py-14 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-6">Serving {page.city} Property Owners</h2>
            {page.localContent.split('\n\n').map((para, i) => (
              <p key={i} className="text-zinc-700 leading-relaxed mb-4">{para}</p>
            ))}
          </div>
        </section>
      )}

      {/* Why Local + Project Types */}
      <section className="py-14 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {page.whyLocal?.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-zinc-900 mb-6">Why Choose a Local {page.city} Contractor</h2>
                <div className="space-y-3">
                  {page.whyLocal.map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="text-white" size={12} />
                      </div>
                      <p className="text-zinc-700 text-sm">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {page.projectTypes?.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-zinc-900 mb-6">Projects We Handle in {page.city}</h2>
                <div className="space-y-3">
                  {page.projectTypes.map((proj) => (
                    <div key={proj} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-zinc-900 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="text-white" size={12} />
                      </div>
                      <p className="text-zinc-700 text-sm">{proj}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <EstimateCTA
        heading={`Get a ${page.city} Estimate`}
        subtext={`See preliminary pricing for your ${page.city} property project. No obligation.`}
        pagePath={`/${pageSlug}`}
      />

      {/* FAQs */}
      {page.faqs?.length > 0 && (
        <section className="py-14 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">{page.city} FAQ</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {page.faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-zinc-900 hover:text-red-600 py-4">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-zinc-600 leading-relaxed pb-4">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Related Pages */}
      {page.relatedPages?.length > 0 && (
        <section className="py-14 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-zinc-900 mb-6 text-center">Related Services in {page.city}</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {page.relatedPages.map((rp) => (
                <Link key={rp.href} to={rp.href} className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-red-600 transition-all group text-center">
                  <p className="font-semibold text-zinc-900 group-hover:text-red-600 transition-colors text-sm">{rp.label}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
};

export default LocalServicePage;
